import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { useRef, useEffect, useState, useContext, useCallback } from "react";
import { ThemeContext } from "../../../../theme/ThemeContext";
import AuthContext from "../../../../auth/AuthContext";
import AssessmentMasterService from "../../../../services/AssessmentMasterService";
import AlertService from "../../../../services/AlertService";
import { Skeleton } from "primereact";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

// Custom plugin for rounded corners at top and bottom
const roundedBarsPlugin = {
  id: 'roundedBars',
  beforeDatasetsDraw(chart) {
    const { ctx } = chart;
    
    chart.data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      
      const gradient = typeof dataset.backgroundColor === 'function' 
        ? dataset.backgroundColor({ chart, datasetIndex, dataIndex: 0 })
        : dataset.backgroundColor;
      
      meta.data.forEach((bar, index) => {
        const dataValue = dataset.data[index];
        
        if (dataValue > 0) {
          const { x, y, width, base } = bar;
          const radius = 12;
          
          ctx.save();
          ctx.fillStyle = gradient;
          
          // Draw rounded rectangle with curves at top and bottom
          ctx.beginPath();
          ctx.moveTo(x - width / 2 + radius, base);
          ctx.lineTo(x + width / 2 - radius, base);
          ctx.quadraticCurveTo(x + width / 2, base, x + width / 2, base - radius);
          ctx.lineTo(x + width / 2, y + radius);
          ctx.quadraticCurveTo(x + width / 2, y, x + width / 2 - radius, y);
          ctx.lineTo(x - width / 2 + radius, y);
          ctx.quadraticCurveTo(x - width / 2, y, x - width / 2, y + radius);
          ctx.lineTo(x - width / 2, base - radius);
          ctx.quadraticCurveTo(x - width / 2, base, x - width / 2 + radius, base);
          ctx.closePath();
          ctx.fill();
          
          ctx.restore();
        }
      });
    });
    
    return false; 
  }
};

const getGradient = (ctx, chartArea, isDark) => {
  const gradient = ctx.createLinearGradient(
    0,
    chartArea.top,
    0,
    chartArea.bottom
  );
  gradient.addColorStop(0, isDark ? "#7B78FF" : "#5A57FF");
  gradient.addColorStop(
    1,
    isDark ? "rgba(123, 120, 255, 0.6)" : "rgba(90, 86, 184, 0.6)"
  );
  return gradient;
};

export default function CourseWiseChart() {
  const authContext = useContext(AuthContext);
  const chartRef = useRef(null);
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";

  const [allData, setAllData] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState("all");
  const [loading, setLoading] = useState(false);

  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [
      {
        label: "Total Assessments",
        data: [],
        backgroundColor: (context) => {
          const chart = context.chart;
          const { ctx, chartArea } = chart;
          if (!chartArea) return isDark ? "#7B78FF" : "#5A57FF";
          return getGradient(ctx, chartArea, isDark);
        },
        borderRadius: 0, 
        barPercentage: 0.3,
        categoryPercentage: 0.8,
      },
    ],
  });

  const getInitials = (str) => {
    if (!str) return "";
    const words = str.match(/\b\w/g) || [];
    return words.join("").toUpperCase();
  };

const loadDashboard = useCallback(async (signal) => {
    setLoading(true);
    try {
      const data = await AssessmentMasterService.GetCourseWiseDashboard(
        authContext,
        signal 
      );

      if (signal?.aborted) return; 

      if (data.isSuccess) {
        setAllData(data.data);
      } else {
        AlertService.error(data.errorMessage);
        setAllData([]);
      }
    } catch (err) {
      if (err.name === "AbortError") return; 
    } finally {
      if (!signal?.aborted) setLoading(false); 
    }
  }, [authContext]);

useEffect(() => {
    const controller = new AbortController(); 
    loadDashboard(controller.signal); 
    return () => controller.abort(); 
  }, [loadDashboard]);

  useEffect(() => {
    const filteredData =
      selectedCourse === "all"
        ? allData
        : allData.filter((d) => d.courseName === selectedCourse);

    setChartData((prev) => ({
      ...prev,
      labels: filteredData.map((item) => item.courseName),
      datasets: [
        {
          ...prev.datasets[0],
          data: filteredData.map((item) => item.count),
          backgroundColor: (context) => {
            const chart = context.chart;
            const { ctx, chartArea } = chart;
            if (!chartArea) return isDark ? "#7B78FF" : "#5A57FF";
            return getGradient(ctx, chartArea, isDark);
          },
        },
      ],
    }));
  }, [allData, selectedCourse, isDark]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { 
      tooltip: {
        filter: function(tooltipItem) {
          return tooltipItem.raw > 0;
        },
      }, 
      legend: { display: false },
      roundedBars: true, 
    },
    scales: {
      x: {
        ticks: {
          color: isDark ? "#e0e0e0" : "#555555",
          font: { size: window.innerWidth < 480 ? 10 : 12, weight: 600, family: "Poppins" },
          autoSkip: true,
          maxRotation: 0,
          minRotation: 0,
          callback: (value, index) => {
            const fullLabel = chartData.labels[index] || "";
            return getInitials(fullLabel);
          },
        },
        grid: { display: false },
      },
      y: {
        ticks: {
          color: isDark ? "#e0e0e0" : "#555555",
          font: { size: window.innerWidth < 480 ? 10 : 12, family: "Poppins" },
        },
        grid: { 
          color: isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
          borderDash: [4, 4],
        },
      },
    },
    elements: {
      bar: {
        borderRadius: 0, 
        barPercentage: window.innerWidth < 480 ? 0.5 : 0.6,
        categoryPercentage: window.innerWidth < 480 ? 0.6 : 0.8,
      },
    },
  };

  return (
    <div
      className="charts d-flex flex-column"
      style={{
        padding: 16,
        minHeight: "337px",
        width: "100%",
        border: `1px solid ${isDark ? "#444" : "#eee"}`,
        borderRadius: "12px",
        backgroundColor: isDark ? "#222" : "#fff",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        {loading ? (
          <>
            <Skeleton width="140px" height="30px" borderRadius="6px" />
            <Skeleton width="100px" height="30px" borderRadius="6px" />
          </>
        ) : (
          <>
            <h6 style={{ margin: 0, fontSize: 16, color: isDark ? "#e0e0e0" : "#333" }}>
              Course-wise Assessments
            </h6>
            <select
              value={selectedCourse}
              onChange={(e) => setSelectedCourse(e.target.value)}
              style={{
                border: "1px solid #ddd",
                borderRadius: 6,
                backgroundColor: isDark ? "#222" : "#fff",
                color: isDark ? "#eaeaea" : "#000",
                padding: "4px 8px",
                fontSize: 13,
                maxWidth: "120px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              title={selectedCourse === "all" ? "All Courses" : selectedCourse}
              disabled={loading}
            >
              <option value="all">All Courses</option>
              {allData.map((c) => (
                <option
                  key={c.courseCode || c.courseName}
                  value={c.courseName}
                >
                  {c.courseName}
                </option>
              ))}
            </select>
          </>
        )}
      </div>

      {/* Chart */}
      <div style={{ flexGrow: 1, position: "relative" }}>
        {loading ? (
          <div
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "space-around",
              gap: "12px",
              padding: "20px 10px",
            }}
          >
            {[...Array(6)].map((_, i) => (
              <Skeleton
                key={i}
                width="45px"
                height={`${70 + i * 30}px`}
                style={{ borderRadius: "12px" }}
              />
            ))}
          </div>
        ) : (
          <Bar ref={chartRef} data={chartData} options={options} plugins={[roundedBarsPlugin]} />
        )}
      </div>
    </div>
  );
}